import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { FolderIcon } from 'base/components/Icon/index';
import theme from 'theme/index';
import ExpandToggle from 'base/components/ExpandToggle/index';
import { getIsMenuOpenMobile } from '../../../../store/ui/menu/selectors';
import { toggleMobileMenu } from '../../../../store/ui/menu/actions';

function isDescendant(older, younger) {
  return (
    !!older.children
      && typeof older.children !== 'function'
      && older.children.some((child) => child === younger || isDescendant(child, younger))
  );
}

const Wrapper = styled.div`
  width: 100%;
`;

const RowWrapper = styled.div`
  display: flex;
  position: relative;
  padding: 15px 15px 15px 27px;
  height: 100%;
  box-sizing: border-box;
  cursor: move;

  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }

  &:active {
    opacity: 1;
  }
  
  ${(props) => (props.canDrag && css`
    cursor: default;
    &:hover {
      opacity: 1;
    }
  `)}
`;

const rowLandingPad = css`
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  * {
    opacity: 0 !important;
  }
  
  &::before {
    background-color: ${theme.colors.primary};
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    width: 100%;
  }
`;

const Row = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  white-space: nowrap;
  padding: 0 0 0 20px;

  ${(props) => (props.isLandingPadActive && rowLandingPad)}
  
  ${(props) => (props.isLandingPadActive && !props.canDrop && css`
    &::before {
      background-color: #e6a8ad;
    }
  `)}
`;

const RowItem = styled.div`
  width: 100%;
  display: flex;
  padding-right: 30px;
  &:hover {
    cursor: pointer;
  }
`;

const RowContent = styled(RowItem)`
  position: relative;
  height: 100%;
  justify-content: space-between;

  ${(props) => props.isSearchMatch && css`
  `}
  ${(props) => props.isSearchFocus && css`
  `}
  
  ${(props) => !props.canDrag && css`
  `}
`;

const Label = styled.span`
  padding: 0 0 0 4px;
  min-width: 0;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DocumentCount = styled.span`
  font-size: ${theme.fontSizes.default};
  color: ${theme.colors.mediumlight}; 
  position: absolute;
  right: 0;
`;


// eslint-disable-next-line react/prefer-stateless-function
class FolderThemeNodeContentRenderer extends Component {
  render() {
    const {
      scaffoldBlockPxWidth,
      toggleChildrenVisibility,
      connectDragPreview,
      connectDragSource,
      isDragging,
      canDrop,
      canDrag,
      node,
      title,
      name,
      draggedNode,
      path,
      treeIndex,
      isSearchMatch,
      isSearchFocus,
      icons,
      buttons,
      className,
      style,
      didDrop,
      swapFrom,
      swapLength,
      swapDepth,
      treeId, // Not needed, but preserved for other renderers
      isOver, // Not needed, but preserved for other renderers
      parentNode, // Needed for dndManager
      documentCountByFolderId,
      isOpenMobile, // eslint-disable-line
      toggleMobileMenu, // eslint-disable-line
      ...otherProps
    } = this.props;

    // Construct the scaffold representing the structure of the tree
    const scaffoldBlockCount = path.length - 1;
    const scaffoldWidth = scaffoldBlockPxWidth * scaffoldBlockCount;

    const isDraggedDescendant = draggedNode && isDescendant(draggedNode, node);
    const isLandingPadActive = !didDrop && isDragging;

    const toggleFolder = () => toggleChildrenVisibility({ node, path, treeIndex });

    const handleClick = () => {
      toggleFolder();
      if (isOpenMobile) {
        toggleMobileMenu();
      }
    };

    const toggleFolderAndStopPropagation = (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggleFolder();
    };

    return (
      <Wrapper {...otherProps}>

        <RowWrapper
          canDrag={canDrag}
          onClick={handleClick}
          style={{ paddingLeft: scaffoldWidth + 12 }}
        >

          <Row
            canDrop={canDrop}
            isLandingPadActive={isLandingPadActive}
            style={{
              opacity: isDraggedDescendant ? 0.5 : 1,
            }}
          >
            {toggleChildrenVisibility
            && node.children
            && (node.children.length > 0 || typeof node.children === 'function')
            && (
            <span>
              <ExpandToggle
                isOpen={node.expanded}
                onClick={toggleFolderAndStopPropagation}
                style={{ position: 'absolute', left: 0 }}
              />

              {node.expanded
                  && !isDragging
                  && (<span style={{ width: scaffoldBlockPxWidth }} />
                  )}
            </span>
            )}
            <RowContent
              isSearchMatch={isSearchMatch}
              isSearchFocus={isSearchFocus}
              canDrag={canDrag}
              ref={(instance) => canDrag && connectDragSource(instance, { dropEffect: 'copy' })}
            >
              <FolderIcon size={theme.fontSizes.default} />
              <Label>{node.name}</Label>

              {(documentCountByFolderId
                  && documentCountByFolderId[node.id]
                  && documentCountByFolderId[node.id] !== 0)
                ? (
                  <DocumentCount>{documentCountByFolderId[node.id]}</DocumentCount>
                )
                : ''}

            </RowContent>
          </Row>
        </RowWrapper>
      </Wrapper>
    );
  }
}

FolderThemeNodeContentRenderer.defaultProps = {
  buttons: [],
  canDrag: false,
  canDrop: false,
  className: '',
  draggedNode: null,
  icons: [],
  isSearchFocus: false,
  isSearchMatch: false,
  parentNode: null,
  style: {},
  subtitle: null,
  swapDepth: null,
  swapFrom: null,
  swapLength: null,
  title: null,
  name: null,
  toggleChildrenVisibility: null,
  documentCountByFolderId: {},
};

FolderThemeNodeContentRenderer.propTypes = {
  buttons: PropTypes.arrayOf(PropTypes.node),
  canDrag: PropTypes.bool,
  className: PropTypes.string,
  icons: PropTypes.arrayOf(PropTypes.node),
  isSearchFocus: PropTypes.bool,
  isSearchMatch: PropTypes.bool,
  node: PropTypes.shape({}).isRequired,
  path: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  scaffoldBlockPxWidth: PropTypes.number.isRequired,
  style: PropTypes.shape({}),
  subtitle: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  swapDepth: PropTypes.number,
  swapFrom: PropTypes.number,
  swapLength: PropTypes.number,
  title: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  name: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  toggleChildrenVisibility: PropTypes.func,
  treeIndex: PropTypes.number.isRequired,
  treeId: PropTypes.string.isRequired,
  documentCountByFolderId: PropTypes.objectOf(PropTypes.any),

  // Drag and drop API functions
  // Drag source
  connectDragPreview: PropTypes.func.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  didDrop: PropTypes.bool.isRequired,
  draggedNode: PropTypes.shape({}),
  isDragging: PropTypes.bool.isRequired,
  parentNode: PropTypes.shape({}), // Needed for dndManager
  // Drop target
  canDrop: PropTypes.bool,
  isOver: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  isOpenMobile: getIsMenuOpenMobile(state),
});
const mapDispatchToProps = {
  toggleMobileMenu,
};
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(FolderThemeNodeContentRenderer);
